import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Clock,
  GripVertical,
  MessageCircle,
  MousePointerClick,
  Plus,
  Save,
  Settings2,
  SlidersHorizontal,
  Trash2,
} from "lucide-react";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminSection } from "@/components/admin/AdminSection";
import { MediaField } from "@/components/admin/MediaField";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useUnsavedChanges } from "@/hooks/useUnsavedChanges";
import { WHATSAPP_PAGE_KEYS } from "@/lib/whatsapp/widget";
import {
  getAdminWhatsappSettings,
  saveWhatsappSettings,
  saveWhatsappQuickAction,
  deleteWhatsappQuickAction,
  reorderWhatsappQuickActions,
} from "@/lib/admin/cms-whatsapp.functions";

export const Route = createFileRoute("/_admin/admin/cms/whatsapp")({
  component: WhatsappChatAdminPage,
});

type Settings = {
  is_enabled: boolean;
  business_name: string;
  country_code: string;
  business_number: string;
  default_message: string;
  button_position: "bottom-right" | "bottom-left";
  button_color: string;
  widget_size: "sm" | "md" | "lg";
  animation_enabled: boolean;
  pulse_interval_seconds: number;
  show_on_desktop: boolean;
  show_on_tablet: boolean;
  show_on_mobile: boolean;
  popup_enabled: boolean;
  popup_delay_seconds: number;
  popup_title: string;
  popup_description: string;
  popup_cta_text: string;
  popup_auto_close_seconds: number;
  popup_remember_session: boolean;
  popup_image_url: string;
  quick_actions_enabled: boolean;
  hours_enabled: boolean;
  opening_time: string;
  closing_time: string;
  working_days: number[];
  timezone: string;
  online_message: string;
  offline_message: string;
  visibility_mode: "all" | "selected";
  included_pages: string[];
  excluded_pages: string[];
};

type QuickAction = {
  id: string;
  title: string;
  icon: string | null;
  message: string;
  display_order: number;
  is_enabled: boolean;
};

const DAYS = [
  { value: 1, label: "Mon" },
  { value: 2, label: "Tue" },
  { value: 3, label: "Wed" },
  { value: 4, label: "Thu" },
  { value: 5, label: "Fri" },
  { value: 6, label: "Sat" },
  { value: 0, label: "Sun" },
];

function str(v: unknown): string {
  return typeof v === "string" ? v : v == null ? "" : String(v);
}

function toForm(row: Record<string, unknown>): Settings {
  return {
    is_enabled: Boolean(row["is_enabled"]),
    business_name: str(row["business_name"]),
    country_code: str(row["country_code"]) || "+91",
    business_number: str(row["business_number"]),
    default_message: str(row["default_message"]),
    button_position: (str(row["button_position"]) as Settings["button_position"]) || "bottom-right",
    button_color: str(row["button_color"]),
    widget_size: (str(row["widget_size"]) as Settings["widget_size"]) || "md",
    animation_enabled: Boolean(row["animation_enabled"]),
    pulse_interval_seconds: Number(row["pulse_interval_seconds"] ?? 20),
    show_on_desktop: Boolean(row["show_on_desktop"]),
    show_on_tablet: Boolean(row["show_on_tablet"]),
    show_on_mobile: Boolean(row["show_on_mobile"]),
    popup_enabled: Boolean(row["popup_enabled"]),
    popup_delay_seconds: Number(row["popup_delay_seconds"] ?? 5),
    popup_title: str(row["popup_title"]),
    popup_description: str(row["popup_description"]),
    popup_cta_text: str(row["popup_cta_text"]),
    popup_auto_close_seconds: Number(row["popup_auto_close_seconds"] ?? 10),
    popup_remember_session: Boolean(row["popup_remember_session"]),
    popup_image_url: str(row["popup_image_url"]),
    quick_actions_enabled: Boolean(row["quick_actions_enabled"]),
    hours_enabled: Boolean(row["hours_enabled"]),
    opening_time: str(row["opening_time"]) || "09:00",
    closing_time: str(row["closing_time"]) || "21:00",
    working_days: Array.isArray(row["working_days"]) ? (row["working_days"] as number[]) : [],
    timezone: str(row["timezone"]) || "Asia/Kolkata",
    online_message: str(row["online_message"]),
    offline_message: str(row["offline_message"]),
    visibility_mode: (str(row["visibility_mode"]) as Settings["visibility_mode"]) || "all",
    included_pages: Array.isArray(row["included_pages"]) ? (row["included_pages"] as string[]) : [],
    excluded_pages: Array.isArray(row["excluded_pages"]) ? (row["excluded_pages"] as string[]) : [],
  };
}

function Toggle({
  label,
  checked,
  onChange,
  hint,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  hint?: string;
}) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-md border p-3">
      <div>
        <p className="text-sm font-medium text-foreground">{label}</p>
        {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
      </div>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

function WhatsappChatAdminPage() {
  const load = useServerFn(getAdminWhatsappSettings);
  const saveSettings = useServerFn(saveWhatsappSettings);
  const saveAction = useServerFn(saveWhatsappQuickAction);
  const removeAction = useServerFn(deleteWhatsappQuickAction);
  const reorder = useServerFn(reorderWhatsappQuickActions);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["cms-admin", "whatsapp"],
    queryFn: () => load(),
  });

  const [form, setForm] = useState<Settings | null>(null);
  const [loaded, setLoaded] = useState<Settings | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);

  useEffect(() => {
    if (!data?.settings) return;
    const next = toForm(data.settings as Record<string, unknown>);
    setForm(next);
    setLoaded(next);
  }, [data]);

  const actions = useMemo(
    () =>
      ((data?.quickActions ?? []) as unknown as QuickAction[])
        .slice()
        .sort((a, b) => a.display_order - b.display_order),
    [data],
  );

  const dirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(loaded), [form, loaded]);
  useUnsavedChanges(dirty);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["cms-admin", "whatsapp"] });
    void qc.invalidateQueries({ queryKey: ["cms"] });
  };

  const settingsMutation = useMutation({
    mutationFn: async () => {
      if (!form) return null;
      return saveSettings({
        data: {
          ...form,
          business_name: form.business_name.trim() || null,
          business_number: form.business_number.trim() || null,
          default_message: form.default_message.trim() || null,
          button_color: form.button_color.trim() || null,
          popup_title: form.popup_title.trim() || null,
          popup_description: form.popup_description.trim() || null,
          popup_cta_text: form.popup_cta_text.trim() || null,
          popup_image_url: form.popup_image_url.trim() || null,
          online_message: form.online_message.trim() || null,
          offline_message: form.offline_message.trim() || null,
        } as never,
      });
    },
    onSuccess: () => {
      toast.success("WhatsApp chat settings saved");
      setLoaded(form);
      refresh();
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Save failed"),
  });

  const actionMutation = useMutation({
    mutationFn: (a: Partial<QuickAction> & { title: string; message: string }) =>
      saveAction({
        data: {
          ...(a.id ? { id: a.id } : {}),
          title: a.title.trim(),
          icon: (a.icon ?? "").trim() || null,
          message: a.message.trim(),
          display_order: a.display_order ?? actions.length + 1,
          is_enabled: a.is_enabled ?? true,
        } as never,
      }),
    onSuccess: () => {
      toast.success("Quick action saved");
      refresh();
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Save failed"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => removeAction({ data: { id } }),
    onSuccess: () => {
      toast.success("Quick action removed");
      refresh();
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Couldn't delete"),
  });

  const reorderMutation = useMutation({
    mutationFn: (ids: string[]) => reorder({ data: { ids } }),
    onSuccess: refresh,
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Reorder failed"),
  });

  if (isLoading || !form) {
    return <p className="text-sm text-muted-foreground">Loading WhatsApp settings…</p>;
  }

  const set = <K extends keyof Settings>(key: K) => (value: Settings[K]) =>
    setForm((f) => (f ? { ...f, [key]: value } : f));

  const togglePage = (list: "included_pages" | "excluded_pages", key: string) =>
    setForm((f) => {
      if (!f) return f;
      const current = f[list];
      return {
        ...f,
        [list]: current.includes(key) ? current.filter((k) => k !== key) : [...current, key],
      };
    });

  const onDrop = (targetId: string) => {
    if (!dragId || dragId === targetId) return;
    const ids = actions.map((a) => a.id);
    const from = ids.indexOf(dragId);
    const to = ids.indexOf(targetId);
    if (from < 0 || to < 0) return;
    ids.splice(to, 0, ids.splice(from, 1)[0]!);
    setDragId(null);
    reorderMutation.mutate(ids);
  };

  return (
    <div className="space-y-4 pb-24">
      <AdminPageHeader
        title="WhatsApp Chat"
        description="Floating WhatsApp chat widget shown on the public website. Everything below is editable — no code changes needed."
      />

      <AdminSection
        number={1}
        icon={Settings2}
        title="General"
        description="Business number, button styling and per-device visibility."
      >
        <div className="grid gap-4 md:grid-cols-2">
          <div className="md:col-span-2">
            <Toggle
              label="Enable widget"
              checked={form.is_enabled}
              onChange={set("is_enabled")}
              hint="Turns the floating WhatsApp button on or off across the website."
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="wa-name" className="text-xs">Business name</Label>
            <Input id="wa-name" value={form.business_name} onChange={(e) => set("business_name")(e.target.value)} />
          </div>
          <div className="grid grid-cols-[7rem_1fr] gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="wa-cc" className="text-xs">Country code</Label>
              <Input id="wa-cc" value={form.country_code} onChange={(e) => set("country_code")(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="wa-num" className="text-xs">Business number</Label>
              <Input id="wa-num" inputMode="tel" value={form.business_number} onChange={(e) => set("business_number")(e.target.value)} />
            </div>
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label htmlFor="wa-msg" className="text-xs">Default pre-filled message</Label>
            <Textarea id="wa-msg" rows={2} value={form.default_message} onChange={(e) => set("default_message")(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Button position</Label>
            <Select value={form.button_position} onValueChange={(v) => set("button_position")(v as Settings["button_position"])}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="bottom-right">Bottom right</SelectItem>
                <SelectItem value="bottom-left">Bottom left</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Widget size</Label>
            <Select value={form.widget_size} onValueChange={(v) => set("widget_size")(v as Settings["widget_size"])}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="sm">Small</SelectItem>
                <SelectItem value="md">Medium</SelectItem>
                <SelectItem value="lg">Large</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="wa-color" className="text-xs">Button colour (optional)</Label>
            <Input id="wa-color" placeholder="#25D366" value={form.button_color} onChange={(e) => set("button_color")(e.target.value)} />
            <p className="text-[11px] text-muted-foreground">Leave empty to use the Healthy Kitchen accent colour.</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="wa-pulse" className="text-xs">Pulse interval (seconds)</Label>
            <Input id="wa-pulse" type="number" min={5} max={600} value={form.pulse_interval_seconds}
              onChange={(e) => set("pulse_interval_seconds")(Number(e.target.value) || 0)} />
          </div>
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <Toggle label="Animation" checked={form.animation_enabled} onChange={set("animation_enabled")} hint="Fade in and periodic pulse." />
          <Toggle label="Show on desktop" checked={form.show_on_desktop} onChange={set("show_on_desktop")} />
          <Toggle label="Show on tablet" checked={form.show_on_tablet} onChange={set("show_on_tablet")} />
          <Toggle label="Show on mobile" checked={form.show_on_mobile} onChange={set("show_on_mobile")} />
        </div>
      </AdminSection>

      <AdminSection
        number={2}
        icon={MessageCircle}
        title="Welcome popup"
        description="Optional message bubble that appears above the button."
      >
        <div className="grid gap-4 md:grid-cols-2">
          <div className="md:col-span-2">
            <Toggle label="Enable welcome popup" checked={form.popup_enabled} onChange={set("popup_enabled")} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="wa-ptitle" className="text-xs">Popup title</Label>
            <Input id="wa-ptitle" value={form.popup_title} onChange={(e) => set("popup_title")(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="wa-pcta" className="text-xs">CTA button text</Label>
            <Input id="wa-pcta" value={form.popup_cta_text} onChange={(e) => set("popup_cta_text")(e.target.value)} />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label htmlFor="wa-pdesc" className="text-xs">Popup description</Label>
            <Textarea id="wa-pdesc" rows={2} value={form.popup_description} onChange={(e) => set("popup_description")(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="wa-pdelay" className="text-xs">Popup delay (seconds)</Label>
            <Input id="wa-pdelay" type="number" min={0} max={120} value={form.popup_delay_seconds}
              onChange={(e) => set("popup_delay_seconds")(Number(e.target.value) || 0)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="wa-pclose" className="text-xs">Auto close after (seconds)</Label>
            <Input id="wa-pclose" type="number" min={0} max={120} value={form.popup_auto_close_seconds}
              onChange={(e) => set("popup_auto_close_seconds")(Number(e.target.value) || 0)} />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label className="text-xs">Popup image (optional)</Label>
            <MediaField value={form.popup_image_url} onChange={set("popup_image_url")} allowUrlInput />
          </div>
        </div>
        <div className="mt-4">
          <Toggle
            label="Remember closed for the session"
            checked={form.popup_remember_session}
            onChange={set("popup_remember_session")}
            hint="Once dismissed, the popup won't reappear during the same browser session."
          />
        </div>
      </AdminSection>

      <AdminSection
        number={3}
        icon={MousePointerClick}
        title="Quick actions"
        description="Buttons inside the popup. Each opens WhatsApp with its own pre-filled message. Drag to reorder."
      >
        <Toggle label="Enable quick actions" checked={form.quick_actions_enabled} onChange={set("quick_actions_enabled")} />
        <ul className="mt-4 space-y-2">
          {actions.map((a) => (
            <li
              key={a.id}
              draggable
              onDragStart={() => setDragId(a.id)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(a.id)}
              className="rounded-md border bg-card p-3"
            >
              <div className="flex items-start gap-3">
                <GripVertical className="mt-2 h-4 w-4 shrink-0 cursor-grab text-muted-foreground" aria-hidden />
                <div className="grid flex-1 gap-2 md:grid-cols-[4rem_1fr]">
                  <Input
                    aria-label="Icon"
                    defaultValue={a.icon ?? ""}
                    onBlur={(e) =>
                      e.target.value !== (a.icon ?? "") &&
                      actionMutation.mutate({ ...a, icon: e.target.value })
                    }
                  />
                  <Input
                    aria-label="Title"
                    defaultValue={a.title}
                    onBlur={(e) =>
                      e.target.value.trim() &&
                      e.target.value !== a.title &&
                      actionMutation.mutate({ ...a, title: e.target.value })
                    }
                  />
                  <Textarea
                    aria-label="Pre-filled message"
                    className="md:col-span-2"
                    rows={2}
                    defaultValue={a.message}
                    onBlur={(e) =>
                      e.target.value.trim() &&
                      e.target.value !== a.message &&
                      actionMutation.mutate({ ...a, message: e.target.value })
                    }
                  />
                </div>
                <div className="flex flex-col items-end gap-2">
                  <Switch
                    checked={a.is_enabled}
                    onCheckedChange={(v) => actionMutation.mutate({ ...a, is_enabled: v })}
                    aria-label={`Enable ${a.title}`}
                  />
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="icon" aria-label={`Delete ${a.title}`}>
                        <Trash2 className="h-4 w-4" aria-hidden />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete this quick action?</AlertDialogTitle>
                        <AlertDialogDescription>
                          "{a.title}" will no longer appear in the WhatsApp popup. This can't be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => deleteMutation.mutate(a.id)}>Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </li>
          ))}
        </ul>
        <Button
          variant="outline"
          className="mt-3 gap-2"
          onClick={() =>
            actionMutation.mutate({
              title: "New quick action",
              icon: "💬",
              message: "Hi, I have a question about Healthy Kitchen.",
              display_order: actions.length + 1,
              is_enabled: true,
            })
          }
        >
          <Plus className="h-4 w-4" aria-hidden /> Add quick action
        </Button>
      </AdminSection>

      <AdminSection
        number={4}
        icon={Clock}
        title="Business hours"
        description="Show an Online / Offline status based on your working hours."
      >
        <Toggle label="Enable business hours" checked={form.hours_enabled} onChange={set("hours_enabled")} />
        <div className="mt-4 grid gap-4 md:grid-cols-3">
          <div className="space-y-1.5">
            <Label htmlFor="wa-open" className="text-xs">Opening time</Label>
            <Input id="wa-open" type="time" value={form.opening_time} onChange={(e) => set("opening_time")(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="wa-close" className="text-xs">Closing time</Label>
            <Input id="wa-close" type="time" value={form.closing_time} onChange={(e) => set("closing_time")(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="wa-tz" className="text-xs">Timezone</Label>
            <Input id="wa-tz" value={form.timezone} onChange={(e) => set("timezone")(e.target.value)} />
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {DAYS.map((d) => {
            const on = form.working_days.includes(d.value);
            return (
              <Button
                key={d.value}
                type="button"
                size="sm"
                variant={on ? "default" : "outline"}
                onClick={() =>
                  set("working_days")(
                    on
                      ? form.working_days.filter((x) => x !== d.value)
                      : [...form.working_days, d.value],
                  )
                }
              >
                {d.label}
              </Button>
            );
          })}
        </div>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="wa-online" className="text-xs">Online message</Label>
            <Textarea id="wa-online" rows={2} value={form.online_message} onChange={(e) => set("online_message")(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="wa-offline" className="text-xs">Offline message</Label>
            <Textarea id="wa-offline" rows={2} value={form.offline_message} onChange={(e) => set("offline_message")(e.target.value)} />
          </div>
        </div>
      </AdminSection>

      <AdminSection
        number={5}
        icon={SlidersHorizontal}
        title="Advanced"
        description="Control exactly which pages show the widget."
      >
        <div className="space-y-1.5">
          <Label className="text-xs">Show on</Label>
          <Select value={form.visibility_mode} onValueChange={(v) => set("visibility_mode")(v as Settings["visibility_mode"])}>
            <SelectTrigger className="md:w-72"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Entire website</SelectItem>
              <SelectItem value="selected">Selected pages only</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {form.visibility_mode === "selected" && (
          <div className="mt-4">
            <p className="text-xs font-medium text-foreground">Included pages</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {WHATSAPP_PAGE_KEYS.map((p) => (
                <Button
                  key={p.key}
                  type="button"
                  size="sm"
                  variant={form.included_pages.includes(p.key) ? "default" : "outline"}
                  onClick={() => togglePage("included_pages", p.key)}
                >
                  {p.label}
                </Button>
              ))}
            </div>
          </div>
        )}
        <div className="mt-4">
          <p className="text-xs font-medium text-foreground">Excluded pages</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {WHATSAPP_PAGE_KEYS.map((p) => (
              <Button
                key={p.key}
                type="button"
                size="sm"
                variant={form.excluded_pages.includes(p.key) ? "destructive" : "outline"}
                onClick={() => togglePage("excluded_pages", p.key)}
              >
                {p.label}
              </Button>
            ))}
          </div>
        </div>
      </AdminSection>

      <div className="sticky bottom-4 flex justify-end gap-2">
        <Button variant="outline" onClick={() => setForm(loaded)} disabled={!dirty || settingsMutation.isPending} className="shadow-lg">
          Cancel
        </Button>
        <Button onClick={() => settingsMutation.mutate()} disabled={!dirty || settingsMutation.isPending} className="gap-2 shadow-lg">
          <Save className="h-4 w-4" aria-hidden />
          {settingsMutation.isPending ? "Saving…" : "Save settings"}
        </Button>
      </div>
    </div>
  );
}
